import { DatabaseFieldType, type DatabaseValue } from '@databases/types/database';

import {
  type FieldReadCellAddress,
  type FieldReadDatabaseSnapshot
} from './field-read-module.ts';

/**
 * Ordered immutable reference compiled from a dependent Page Link value.
 */
export type ReferencedPageLink = {
  readonly key: string;
  readonly contentId: string;
  readonly ari: string | undefined;
};

function compileReferences(
  value: DatabaseValue | undefined
): readonly ReferencedPageLink[] {
  if (value?.type !== DatabaseFieldType.PAGE_LINK) {
    return [];
  }

  return value.value.flatMap((reference) => {
    const contentId = 'contentId' in reference ? reference.contentId : undefined;

    return typeof contentId === 'string'
      ? [
          Object.freeze({
            key: reference._id,
            contentId,
            ari:
              'ari' in reference && typeof reference.ari === 'string' && reference.ari
                ? reference.ari
                : undefined
          })
        ]
      : [];
  });
}

/**
 * Resolves ordered references from a configured Page Link in one mount snapshot.
 */
export function getReferencedPageLinks(
  database: FieldReadDatabaseSnapshot,
  address: FieldReadCellAddress,
  referencedFieldId: string
): readonly ReferencedPageLink[] {
  const referencedField = database.getField(referencedFieldId);

  if (referencedField?.type !== DatabaseFieldType.PAGE_LINK) {
    return [];
  }

  const storedValue = database.getValue({
    entryId: address.entryId,
    fieldId: referencedFieldId
  });

  return compileReferences(storedValue ?? database.getDefaultValue(referencedFieldId));
}
